import { useAtomValue } from 'jotai';

import { offlineBackendUnreachableAtom } from '@/atoms/offline-recording';
import { useIsOfflineRecordingEnabled } from '@/features/flags';
import { useOfflineRecorder } from '@/hooks/desktop/useOfflineRecorder';
import { isTauri } from '@/tauri/utils';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';

import { useNavigatorOnline } from './useNavigatorOnline';

/**
 * Point-in-time browser-offline read, for callers that act at a moment rather
 * than render on a value — `handleStateChange` in `useRecordingButton` and the
 * New Session route decision. Sampling at the click is the freshest possible
 * answer there, so these callers deliberately do NOT use
 * {@link useNavigatorOnline}; anything that RENDERS from the value must.
 */
export const isBrowserDefinitelyOfflineNow = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return navigator.onLine === false;
};

export interface OfflineCaptureModeState {
  canOfflineCapture: boolean;
  isOfflineCaptureMode: boolean;
}

/**
 * Reactive predicate: should the app behave as if it's in offline capture mode
 * right now?
 *
 * This is the single source of truth the offline-capture surfaces share so they
 * never disagree about "are we offline" — currently the session-header patient
 * field (APP-6501): when true it drops the server-backed search/suggestions and
 * captures a typed-only patient identifier.
 *
 * Mirrors the gate in {@link useRecordingButton} (INTERIM, APP-6344): offline
 * capture is only possible on desktop, with a cached identity (a prior online
 * login). It's *active* when the real connectivity monitor reports
 * `offline`/`backend_unreachable`, or the interim dev toggle
 * (`window.__heidiOfflineTest`, non-prod only) is set. Gated behind the
 * offline-recording flag per the offline rollout convention (every visible
 * offline surface gates on `useIsOfflineRecordingEnabled()`) — the flag is the
 * one master switch, so capture stays live on production builds when it's on.
 */
export function useOfflineCaptureModeState(): OfflineCaptureModeState {
  const isOfflineRecordingEnabled = useIsOfflineRecordingEnabled();
  const isBackendUnreachable = useAtomValue(offlineBackendUnreachableAtom);
  const { canRecordOffline } = useOfflineRecorder();
  // Subscribed rather than read during render, so the "reactive predicate" in
  // the docblock above is true of the browser half too. Called before the early
  // return to keep hook order unconditional.
  const browserIsOnline = useNavigatorOnline();

  const canOfflineCapture = isOfflineRecordingEnabled && isTauri() && canRecordOffline;

  if (!canOfflineCapture) {
    return { canOfflineCapture: false, isOfflineCaptureMode: false };
  }

  // INTERIM (APP-6344): `__heidiOfflineTest` forces offline mode in dev because
  // the Rust backend-reachability monitor only lands in US2 (APP-6182). Read at
  // render time — non-reactive, which is acceptable for a dev-only affordance.
  // Scoped to non-prod so the test toggle never becomes a live offline override
  // on a shipped production build; the connectivity atom is the real signal and
  // drives this reactively in prod.
  const offlineTestModeOn =
    getEnvironment() !== ENV_NAME.PROD &&
    (window as { __heidiOfflineTest?: boolean }).__heidiOfflineTest === true;

  return {
    canOfflineCapture,
    isOfflineCaptureMode: isBackendUnreachable || !browserIsOnline || offlineTestModeOn,
  };
}

export function useIsOfflineCaptureMode(): boolean {
  return useOfflineCaptureModeState().isOfflineCaptureMode;
}
